import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  StickyNote, 
  Plus, 
  Calendar, 
  ClipboardList, 
  Search, 
  Edit2, 
  Trash2, 
  X, 
  Check,
  Clock,
  ChevronLeft,
  ChevronRight,
  User
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Whiteboard = ({ user }) => {
  const [projects, setProjects] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notesLoading, setNotesLoading] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNoteContent, setNewNoteContent] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [notesPage, setNotesPage] = useState(1);
  const [notesTotalPages, setNotesTotalPages] = useState(1);
  const [notesTotal, setNotesTotal] = useState(0);

  useEffect(() => {
    fetchProjects();
    fetchNotes();
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchNotes();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, notesPage]);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(res.data || []);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotes = async () => {
    try {
      setNotesLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: notesPage.toString(),
        page_size: '10'
      });
      if (searchTerm) params.append('search', searchTerm);
      
      const res = await axios.get(`${API_URL}/whiteboard-notes?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotes(res.data.notes || []);
      setNotesTotalPages(res.data.total_pages || 1);
      setNotesTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    } finally {
      setNotesLoading(false);
    }
  };

  const calculateDaysRemaining = (deadline) => {
    if (!deadline) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadlineDate = new Date(deadline);
    deadlineDate.setHours(0, 0, 0, 0);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getDeadlineText = (deadline) => {
    const days = calculateDaysRemaining(deadline);
    if (days === null) return 'No deadline';
    if (days < 0) return `${Math.abs(days)} days overdue`;
    if (days === 0) return 'Due today!';
    if (days === 1) return '1 day to go';
    return `${days} days to go`;
  };

  const getDeadlineColor = (deadline) => {
    const days = calculateDaysRemaining(deadline);
    if (days === null) return '#6B7280';
    if (days < 0) return '#DC2626'; // Red - overdue
    if (days <= 3) return '#DC2626'; // Red - urgent
    if (days <= 7) return '#D97706'; // Orange - soon
    return '#059669'; // Green - plenty of time
  };

  const handleAddNote = async () => {
    if (!newNoteContent.trim()) {
      toast.error('Please enter note content');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/whiteboard-notes`, {
        content: newNoteContent.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Note added successfully');
      setNewNoteContent('');
      setShowAddNote(false);
      fetchNotes();
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error('Failed to add note');
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editContent.trim()) {
      toast.error('Please enter note content');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/whiteboard-notes/${noteId}`, {
        content: editContent.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Note updated successfully');
      setEditingNote(null);
      setEditContent('');
      fetchNotes();
    } catch (error) {
      console.error('Failed to update note:', error);
      toast.error('Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/whiteboard-notes/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Note deleted successfully');
      fetchNotes();
    } catch (error) {
      console.error('Failed to delete note:', error);
      toast.error('Failed to delete note');
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="content-area" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="content-area">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <StickyNote size={28} style={{ color: '#D97706' }} />
            <h1 style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)' }}>Whiteboard</h1>
          </div>
          <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
            Project overview and meeting notes
          </p>
        </div>
      </div>

      {/* Projects as Yellow Sticky Notes */}
      <div style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ClipboardList size={18} style={{ color: '#D97706' }} />
          Projects ({projects.length})
        </h2>
        
        {projects.length === 0 ? (
          <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
            <StickyNote size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
            <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No projects yet</h3>
            <p style={{ margin: 0, color: 'var(--text-tertiary)' }}>Projects will appear here as sticky notes</p>
          </div>
        ) : (
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            gap: '16px' 
          }}>
            {projects.map((project, index) => {
              const taskStats = project.task_stats || { total: 0, pending: 0, in_progress: 0, completed: 0 };
              const deadlineColor = getDeadlineColor(project.deadline);
              
              return (
                <div
                  key={project.id}
                  style={{
                    backgroundColor: '#FEF3C7',
                    borderRadius: '2px',
                    padding: '16px',
                    width: '200px',
                    minHeight: '140px',
                    boxShadow: '2px 4px 8px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)',
                    transform: `rotate(${(index % 5 - 2) * 1.2}deg)`,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    cursor: 'default',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    borderTop: '28px solid #F59E0B'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'rotate(0deg) scale(1.05)';
                    e.currentTarget.style.boxShadow = '4px 8px 16px rgba(0, 0, 0, 0.2)';
                    e.currentTarget.style.zIndex = '10';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = `rotate(${(index % 5 - 2) * 1.2}deg)`;
                    e.currentTarget.style.boxShadow = '2px 4px 8px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.zIndex = '1';
                  }}
                >
                  {/* Project Name */}
                  <div>
                    <h3 style={{ 
                      margin: '0 0 8px 0', 
                      fontSize: '14px', 
                      fontWeight: '700', 
                      color: '#78350F',
                      wordBreak: 'break-word',
                      lineHeight: '1.3'
                    }}>
                      {project.name}
                    </h3>
                  </div>

                  {/* Stats */}
                  <div>
                    {/* Task Count */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px', 
                      marginBottom: '8px',
                      fontSize: '12px',
                      color: '#92400E'
                    }}>
                      <ClipboardList size={12} />
                      <span style={{ fontWeight: '600' }}>{taskStats.total} Tasks</span>
                    </div>

                    {/* Deadline */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px',
                      fontSize: '11px',
                      fontWeight: '700',
                      color: deadlineColor,
                      backgroundColor: `${deadlineColor}15`,
                      padding: '4px 6px',
                      borderRadius: '4px'
                    }}>
                      <Clock size={11} />
                      <span>{getDeadlineText(project.deadline)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Meeting Notes Section */}
      <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
            <StickyNote size={18} style={{ color: '#059669' }} />
            Meeting Notes ({notesTotal})
          </h2>
          
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
              <input
                type="text"
                placeholder="Search notes..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setNotesPage(1);
                }}
                style={{
                  padding: '8px 10px 8px 34px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  fontSize: '13px',
                  width: '200px',
                  backgroundColor: 'var(--bg-secondary)'
                }}
              />
            </div>
            
            {/* Add Note Button */}
            <button 
              onClick={() => setShowAddNote(true)}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px', 
                padding: '8px 14px', 
                fontSize: '13px',
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              <Plus size={16} /> Add Note
            </button>
          </div>
        </div>

        {/* Add Note Form */}
        {showAddNote && (
          <div style={{ 
            padding: '16px', 
            marginBottom: '20px', 
            backgroundColor: '#D1FAE5',
            borderRadius: '2px',
            borderTop: '24px solid #059669',
            boxShadow: '2px 4px 8px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#065F46' }}>New Meeting Note</h3>
              <button onClick={() => { setShowAddNote(false); setNewNoteContent(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={18} style={{ color: '#047857' }} />
              </button>
            </div>
            <textarea
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              placeholder="Enter your meeting note or finalized item..."
              style={{
                width: '100%',
                minHeight: '80px',
                padding: '10px',
                border: '1px solid #059669',
                borderRadius: '4px',
                fontSize: '13px',
                resize: 'vertical',
                marginBottom: '12px',
                backgroundColor: '#ECFDF5'
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button 
                onClick={() => { setShowAddNote(false); setNewNoteContent(''); }}
                style={{ 
                  padding: '6px 14px', 
                  background: 'none', 
                  border: '1px solid #059669', 
                  borderRadius: '4px', 
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#047857'
                }}
              >
                Cancel
              </button>
              <button 
                onClick={handleAddNote}
                style={{ 
                  padding: '6px 14px',
                  backgroundColor: '#059669',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                Save Note
              </button>
            </div>
          </div>
        )}

        {/* Notes List */}
        {notesLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <div className="loading-spinner"></div>
          </div>
        ) : notes.length === 0 ? (
          <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
            <StickyNote size={48} style={{ color: 'var(--text-tertiary)', marginBottom: '16px' }} />
            <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-primary)' }}>No meeting notes yet</h3>
            <p style={{ margin: 0, color: 'var(--text-tertiary)' }}>Add notes to track meeting decisions and action items</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {notes.map((note) => (
                <div 
                  key={note.id} 
                  onClick={() => {
                    // Only start editing if user can edit and not already editing
                    if ((note.created_by === user?.id || user?.role === 'superadmin' || user?.role === 'ceo') && editingNote !== note.id) {
                      setEditingNote(note.id);
                      setEditContent(note.content);
                    }
                  }}
                  style={{ 
                    padding: '14px',
                    backgroundColor: '#D1FAE5',
                    borderRadius: '2px',
                    borderLeft: '4px solid #059669',
                    boxShadow: '1px 2px 4px rgba(0, 0, 0, 0.1)',
                    cursor: (note.created_by === user?.id || user?.role === 'superadmin' || user?.role === 'ceo') ? 'pointer' : 'default',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (note.created_by === user?.id || user?.role === 'superadmin' || user?.role === 'ceo') {
                      e.currentTarget.style.backgroundColor = '#A7F3D0';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#D1FAE5';
                  }}
                >
                  {editingNote === note.id ? (
                    <div onClick={(e) => e.stopPropagation()}>
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        style={{
                          width: '100%',
                          minHeight: '60px',
                          padding: '10px',
                          border: '1px solid #059669',
                          borderRadius: '4px',
                          fontSize: '13px',
                          resize: 'vertical',
                          marginBottom: '10px',
                          backgroundColor: '#ECFDF5'
                        }}
                        autoFocus
                      />
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                        <button 
                          onClick={() => { setEditingNote(null); setEditContent(''); }}
                          style={{ 
                            padding: '4px 10px', 
                            background: 'none', 
                            border: '1px solid #059669', 
                            borderRadius: '4px', 
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12px',
                            color: '#047857'
                          }}
                        >
                          <X size={12} /> Cancel
                        </button>
                        <button 
                          onClick={() => handleUpdateNote(note.id)}
                          style={{ 
                            padding: '4px 10px',
                            backgroundColor: '#059669',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12px'
                          }}
                        >
                          <Check size={12} /> Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p style={{ 
                        margin: '0 0 10px 0', 
                        fontSize: '13px', 
                        color: '#065F46',
                        lineHeight: '1.5',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {note.content}
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '11px', color: '#047857' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <User size={11} />
                            {note.created_by_name || 'Unknown User'}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Calendar size={11} />
                            {formatDateTime(note.created_at)}
                          </span>
                          {note.updated_at && (
                            <span style={{ fontStyle: 'italic' }}>
                              (edited)
                            </span>
                          )}
                        </div>
                        {(note.created_by === user?.id || user?.role === 'superadmin' || user?.role === 'ceo') && (
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button 
                              onClick={() => { setEditingNote(note.id); setEditContent(note.content); }}
                              style={{ 
                                padding: '4px', 
                                background: 'none', 
                                border: 'none', 
                                cursor: 'pointer',
                                color: '#059669'
                              }}
                              title="Edit note"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button 
                              onClick={() => handleDeleteNote(note.id)}
                              style={{ 
                                padding: '4px', 
                                background: 'none', 
                                border: 'none', 
                                cursor: 'pointer',
                                color: '#DC2626'
                              }}
                              title="Delete note"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            {notesTotalPages > 1 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: '16px', 
                marginTop: '20px' 
              }}>
                <button
                  onClick={() => setNotesPage(p => Math.max(1, p - 1))}
                  disabled={notesPage === 1}
                  style={{
                    padding: '6px 14px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    backgroundColor: notesPage === 1 ? 'var(--bg-secondary)' : 'white',
                    cursor: notesPage === 1 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    opacity: notesPage === 1 ? 0.5 : 1,
                    fontSize: '13px'
                  }}
                >
                  <ChevronLeft size={14} /> Prev
                </button>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {notesPage} / {notesTotalPages}
                </span>
                <button
                  onClick={() => setNotesPage(p => Math.min(notesTotalPages, p + 1))}
                  disabled={notesPage === notesTotalPages}
                  style={{
                    padding: '6px 14px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    backgroundColor: notesPage === notesTotalPages ? 'var(--bg-secondary)' : 'white',
                    cursor: notesPage === notesTotalPages ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    opacity: notesPage === notesTotalPages ? 0.5 : 1,
                    fontSize: '13px'
                  }}
                >
                  Next <ChevronRight size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Whiteboard;
